import { TICKET_STATUS_CATEGORY } from "constants/tickets";

/**
 * Cancel-cascade display logic (SOUND-576) — extracted as PURE functions so it
 * is machine-verifiable. sound-app has no component-test harness, so anything
 * that could be *wrong* (deriving, counting, pluralising) belongs here rather
 * than inline in a component, where a human click is the only check.
 *
 * The backend cascades `canceled`/`withdrawn` to children in the todo /
 * in_progress categories and leaves resolved/declined ones alone. These helpers
 * describe that to the user BEFORE they confirm an irreversible transition, and
 * report what actually happened afterwards.
 */

// Open == not yet finished. Mirrors the backend's `!isTicketTerminal(status)`,
// expressed in the frontend's category vocabulary.
const OPEN_CATEGORIES = ["todo", "in_progress"];

/**
 * How many of a ticket's sub-tasks a cancel/withdraw would take down.
 *
 * Prefers the server-sent `statusCategory` and falls back to deriving it from
 * `status`. The fallback is defensive rather than routine — every child in prod
 * carries a statusCategory today — but a category is the only thing that must
 * never be guessed, so an unknown status counts as NOT open (better to
 * under-promise in a warning than to claim we will cancel something we won't).
 *
 * @param {Array<{status?: string, statusCategory?: string}>} [children]
 * @returns {number}
 */
export function countOpenSubtasks(children) {
  if (!Array.isArray(children)) return 0;
  return children.filter((c) => {
    if (!c) return false;
    const category = c.statusCategory || TICKET_STATUS_CATEGORY[c.status];
    return OPEN_CATEGORIES.includes(category);
  }).length;
}

/**
 * The consequence line shown in the confirm panel, or null when there is
 * nothing to warn about (so the caller can render conditionally on the value
 * alone).
 *
 * Singular matters: 19 of the 29 prod parents with open children have exactly
 * ONE, so "1 open sub-tasks" would be the copy most people actually saw. And
 * the second sentence is not filler — 22 of those 29 also have finished
 * children, so most users need telling that completed work survives.
 *
 * @param {number} openCount
 * @returns {string|null}
 */
export function cascadePrompt(openCount) {
  if (!Number.isFinite(openCount) || openCount <= 0) return null;
  const noun = openCount === 1 ? "sub-task" : "sub-tasks";
  return `Also cancels ${openCount} open ${noun}. Completed sub-tasks are kept.`;
}

/**
 * Describe the outcome for a toast, from the API's `meta` block.
 *
 * Returns null when there was no cascade (a non-cancel transition, or a ticket
 * with no open children) so the caller keeps its plain status toast.
 *
 * A PARTIAL cascade is deliberately `warning`, not `success`: the ticket really
 * was cancelled, but some sub-tasks were not, and reporting that as a clean
 * success would hide work that still needs a human. In that case `title`
 * replaces the caller's status title; on success `title` is null and the caller
 * keeps its own.
 *
 * @param {{canceledChildren?: number, failedChildren?: number}} [meta]
 * @returns {{tone: "success"|"warning", title: string|null, description: string}|null}
 */
export function summarizeCascade(meta) {
  if (!meta) return null;
  const canceled = Number(meta.canceledChildren) || 0;
  const failed = Number(meta.failedChildren) || 0;

  if (failed > 0) {
    const noun = failed === 1 ? "sub-task" : "sub-tasks";
    return {
      tone: "warning",
      title: "Ticket cancelled — some sub-tasks were not",
      description: `${canceled} cancelled, ${failed} failed. Check the affected ${noun} and cancel manually.`,
    };
  }

  if (canceled > 0) {
    const noun = canceled === 1 ? "sub-task" : "sub-tasks";
    return {
      tone: "success",
      title: null, // caller keeps its "Status → Canceled" title
      description: `${canceled} ${noun} cancelled.`,
    };
  }

  return null;
}
